import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getCachedIdToken, terrainTileUrlTemplate, isAtlasApiUrl } from '../atlas/atlasApi';
import { fmtMiles, publicCategoryLabel } from './format';
import { slopeBandFor, DEFAULT_MAX_SLOPE_PERCENT, STEEP_COLOR } from './slopeBands';
// Colours, opacities and widths shared with the Atlas chat `land_map` renderer
// (../atlas/maps/land_map_colors.json is the single source of truth).
import {
  ANCHOR_COLOR, ANCHOR_LABEL_COLOR, EXTERNALITY_COLOR, PUBLIC_PLACE_COLOR, PUBLIC_PLACE_LABEL_COLOR, PLACES_RING_COLOR,
  PARCEL_COLOR, PARCEL_FILL_OPACITY, PARCEL_LINE_WIDTH, PARCEL_CASING_COLOR, PARCEL_CASING_WIDTH, PARCEL_CASING_OPACITY,
  ZONE_COLOR, ZONE_FILL_OPACITY, ZONE_LINE_WIDTH, ZONE_DASH, ZONE_LINE_OPACITY,
  CANOPY_COLOR, CANOPY_PARCEL_OPACITY, CANOPY_SURROUND_OPACITY,
  FOOTPRINT_COLOR, FOOTPRINT_PARCEL_OPACITY, FOOTPRINT_CROSSING_OPACITY, FOOTPRINT_SURROUND_OPACITY,
  RADIUS_RING_COLOR, COMP_NEW_COLOR, COMP_EXISTING_COLOR, TERRAIN_LOW_COLOR, TERRAIN_HIGH_COLOR,
  SUBJECT_PIN_COLOR, PREVIEW_PIN_COLOR, FLOOD_COLORS,
} from '../atlas/maps/landMapColors';

const DEFAULT_CENTER = [-98.35, 39.5];
const DEFAULT_ZOOM = 3.3;
const RESULT_ZOOM = 15.4;
const PREVIEW_ZOOM = 13;
const JOB_RADII_MILES = [1, 2, 5, 10];
const MILE_METERS = 1609.344;
const FEET_PER_METER = 3.28084;

const STYLE_URLS = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

// Topography: Mapbox's global DEM drives the hillshade and the optional 3D
// terrain; the Terrain v2 vector tileset supplies contour lines (10 m at
// z14+, coarser when zoomed out; index = 5 / 10 marks every fifth line).
const DEM_TILESET = 'mapbox://mapbox.mapbox-terrain-dem-v1';
const TERRAIN_TILESET = 'mapbox://mapbox.mapbox-terrain-v2';
const DEM_SOURCE = 'le-dem';
const USGS_DEM_SOURCE = 'le-usgs-dem';   // 1 m USGS lidar as Terrain-RGB via the dev API
const USGS_DEM_MIN_ZOOM = 12;
const USGS_DEM_MAX_ZOOM = 17;
const TERRAIN_DEM_SOURCE = 'le-dem-terrain'; // separate from the hillshade DEM: sharing one lowers hillshade resolution
const BUILDINGS_LAYER = 'le-buildings-3d';
const BUILDINGS_MIN_ZOOM = 14.5;
const CONTOUR_SOURCE = 'le-terrain';
const PITCH_3D = 58;
const FOCUS_ZOOM = 16.4;
const PUBLIC_PLACE_LABELLED = 6;             // label only the nearest few; the list carries the rest
const PLACES_LAYERS = ['le-places-ring-line', 'le-externalities-circle', 'le-public-places-circle', 'le-public-places-label', 'le-anchors-circle', 'le-anchors-label'];
// Tree canopy: a darker leaf green than the anchors' green and the public
// places' blue-teal; parcel patches solid, surroundings faded (colours in
// landMapColors).
const CANOPY_SOURCE = 'le-canopy';
const CANOPY_LAYERS = ['le-canopy-fill', 'le-canopy-patch-line', 'le-canopy-boundary-line', 'le-canopy-radius-line'];
// Building footprints (2.2.0): the API's Overture outlines in slate blue — a
// cool hue nothing else on the map uses (parcel yellow, zone violet, canopy
// green, comps blue/violet). On-parcel solid, crossing hatched, neighbours
// faint. These are the API's outlines; the basemap's `le-buildings-3d`
// extrusions are a separate, untouched layer. Colours in landMapColors.
const FOOTPRINT_SOURCE = 'le-footprints';
const FOOTPRINT_PARCEL_LAYERS = ['le-footprints-fill', 'le-footprints-crossing-hatch', 'le-footprints-line', 'le-footprints-crossing-line', 'le-footprints-boundary-line'];
const FOOTPRINT_SURROUND_LAYERS = ['le-footprints-surround-fill', 'le-footprints-surround-line', 'le-footprints-radius-line'];
const FOOTPRINT_LAYERS = [...FOOTPRINT_PARCEL_LAYERS, ...FOOTPRINT_SURROUND_LAYERS];
const FOOTPRINT_HATCH_IMAGE = 'le-hatch-footprint';
// FEMA flood zones (2.3.0): `site_screening.layers.flood_zone.data.geojson`.
// Zones clipped to the lot (location "parcel") solid, the ring around it
// ("surroundings", only when a radius was requested) faded, base flood
// elevation lines dashed with their elevation. Only the classes that restrict
// building are filled; minimal-hazard X would just grey out the lot.
const FLOOD_SOURCE = 'le-flood';
const FLOOD_LAYERS = ['le-flood-fill', 'le-flood-line', 'le-flood-bfe-line', 'le-flood-bfe-label'];
const FLOOD_MAP_CLASSES = ['floodway', 'high_risk', 'moderate'];
const FLOOD_CLASS_TEXT = { floodway: 'Regulatory floodway', high_risk: '1% annual chance flood zone (high risk)', moderate: '0.2% annual chance flood zone (moderate)' };
// Flood fills sit above the parcel elevation grid (so they read through it) and
// below the contours, canopy, footprints and the parcel outline.
const FLOOD_BEFORE_CANDIDATES = ['le-terrain-contour-casing', 'le-canopy-fill', 'le-footprints-surround-fill', 'le-footprints-fill', 'le-parcel-casing'];
const PARCEL_MAX_ZOOM = 18.3;
const TERRAIN_RAMP = ['#fff7db', '#fbbf24', '#c2410c', '#7c2d12'];
// Parcel contour sweep: a highlight band runs from the highest contour down
// to the lowest once per period, then starts again at the top.
const CONTOUR_SWEEP_MS = 3400;
const CONTOUR_LAYERS = ['le-terrain-contour-casing', 'le-terrain-contour-line', 'le-terrain-contour-glow', 'le-terrain-contour-label'];
const STEEP_LAYERS = ['le-terrain-steep-hatch'];

const FALLBACK_TOKEN = 'pk.eyJ1IjoibWljaGFlbC1ydCIsImEiOiJjbTFmY2gxZGYydjBhMmptejRkMXY3NnE4In0.HLgD2rXqws3hQ0Wr7X7PQw';

/** Geodesic-ish circle polygon (same construction as LocalDemographics). */
export function circleCoordinates(center, radiusMeters, numPoints = 96) {
  const coordinates = [];
  const earthRadius = 6378137;
  const latOffset = (radiusMeters / earthRadius) * (180 / Math.PI);
  const lngOffset = latOffset / Math.cos((center[1] * Math.PI) / 180);
  for (let i = 0; i < numPoints; i += 1) {
    const angle = ((i * 360) / numPoints) * (Math.PI / 180);
    coordinates.push([center[0] + lngOffset * Math.sin(angle), center[1] + latOffset * Math.cos(angle)]);
  }
  coordinates.push(coordinates[0]);
  return coordinates;
}

/** Contour interval served by Terrain v2 at a given zoom (meters). */
export function contourIntervalMeters(zoom) {
  if (!Number.isFinite(zoom)) return null;
  if (zoom >= 14) return 10;
  if (zoom >= 13) return 20;
  if (zoom >= 12) return 50;
  if (zoom >= 11) return 100;
  if (zoom >= 10) return 200;
  if (zoom >= 9) return 500;
  return null;
}

/** [[minLng, minLat], [maxLng, maxLat]] of a GeoJSON (Multi)Polygon, or null. */
export function parcelBounds(geometry) {
  if (!geometry || !Array.isArray(geometry.coordinates)) return null;
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.type === 'MultiPolygon' ? geometry.coordinates : [];
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  polygons.forEach((rings) => rings.forEach((ring) => ring.forEach(([x, y]) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  })));
  if (!Number.isFinite(minX)) return null;
  return [[minX, minY], [maxX, maxY]];
}

/** Darken/lighten a hex color by hillshade s in [0,1] (0.55 full shadow .. 1.2 lit). */
function shadeRgb(rgb, s) {
  const shade = Number.isFinite(s) ? Math.max(0, Math.min(1, s)) : 0.7;
  const k = 0.55 + 0.65 * shade;
  return `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c * k))).toString(16).padStart(2, '0')).join('')}`;
}
const hexRgb = (hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

/** Slope band color for a per-cell slope percent, shaded like the elevation ramp; grey where slope is unknown. */
export function shadedSlopeColor(slopePercent, s) {
  const band = slopeBandFor(slopePercent);
  return shadeRgb(band ? hexRgb(band.color) : [156, 163, 175], s);
}

/** Diagonal hatch pattern (white by default, drawn over the solid red of cells steeper than the chosen max slope). */
const HATCH_IMAGE = 'le-hatch-steep';
function ensureHatchImage(map, id = HATCH_IMAGE, rgb = [255, 255, 255], alpha = 215) {
  if (map.hasImage(id)) return;
  const size = 10;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const on = (x + y) % size < 3;
      const i = (y * size + x) * 4;
      data[i] = rgb[0]; data[i + 1] = rgb[1]; data[i + 2] = rgb[2]; data[i + 3] = on ? alpha : 0;
    }
  }
  try { map.addImage(id, { width: size, height: size, data }, { pixelRatio: 1 }); } catch (e) { /* style swapped mid-add; the next sync retries */ }
}

/** Elevation ramp color for t in [0,1], darkened/lightened by hillshade s in [0,1]. */
export function shadedElevationColor(t, s) {
  const stops = TERRAIN_RAMP.map((hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)]);
  const x = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(x));
  const f = x - i;
  return shadeRgb([0, 1, 2].map((c) => stops[i][c] + (stops[i + 1][c] - stops[i][c]) * f), s);
}

// The subject and preview pins are map symbols, not HTML markers. Symbols are
// drawn on the terrain surface with the same elevation data as the imagery, so
// they stay on the parcel while terrain tiles load, when the camera is pitched
// and when the map is panned zoomed out; HTML markers are projected separately
// and drifted in exactly those cases.
const PIN_IMAGES = { subject: { id: 'le-pin-subject', fill: SUBJECT_PIN_COLOR }, preview: { id: 'le-pin-preview', fill: PREVIEW_PIN_COLOR } };
const PIN_SOURCE = 'le-pins';

function pinSvg(fill) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="68" viewBox="0 0 26 34"><path d="M13 1C6.4 1 1 6.3 1 12.8 1 21.6 13 33 13 33s12-11.4 12-20.2C25 6.3 19.6 1 13 1z" fill="${fill}" stroke="#ffffff" stroke-width="2"/><circle cx="13" cy="12.8" r="4.2" fill="#ffffff"/></svg>`;
}

/** Register both pin images on the map (idempotent; images vanish on a style swap). */
function ensurePinImages(map) {
  if (!map || typeof map.addImage !== 'function' || typeof map.hasImage !== 'function' || typeof Image === 'undefined') return Promise.resolve(false);
  const missing = Object.values(PIN_IMAGES).filter(({ id }) => !map.hasImage(id));
  if (!missing.length) return Promise.resolve(true);
  // One in-flight load per map: concurrent syncs share it instead of racing.
  if (map.__lePinImagesLoading) return map.__lePinImagesLoading;
  const jobs = missing.map(({ id, fill }) => new Promise((resolve) => {
    const img = new Image(52, 68);
    img.onload = () => {
      try { if (!map.hasImage(id)) map.addImage(id, img, { pixelRatio: 2 }); } catch (e) { /* style swapped mid-load; the next sync retries */ }
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(pinSvg(fill))}`;
  }));
  map.__lePinImagesLoading = Promise.all(jobs).then((results) => { map.__lePinImagesLoading = null; return results.every(Boolean); });
  return map.__lePinImagesLoading;
}

/** Canopy features the layer will draw (patches, boundary, search circle); [] when there is no usable geometry. */
export function canopyFeatures(canopy) {
  if (!canopy || canopy.type !== 'FeatureCollection' || !Array.isArray(canopy.features)) return [];
  return canopy.features.filter((f) => f && f.geometry && Array.isArray(f.geometry.coordinates) && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'));
}

const isPolygonFeature = (f) => f && f.geometry && Array.isArray(f.geometry.coordinates) && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon');

/**
 * Footprint features the layer will draw, tagged by `location`:
 * `parcel` / `crossing` from `buildings[]` (the source of truth for the lot's
 * own outlines — `on_parcel: false` rows are neighbours' structures that cross
 * the line), plus `surroundings` / `parcel_boundary` / `search_radius` from the
 * optional `geojson` (its own parcel/crossing copies are skipped as duplicates).
 * [] when there is no usable geometry.
 */
export function footprintFeatures(bf) {
  if (!bf || typeof bf !== 'object') return [];
  const features = [];
  const seen = new Set();
  (Array.isArray(bf.buildings) ? bf.buildings : []).forEach((b) => {
    if (!isPolygonFeature({ geometry: b?.geometry })) return;
    const onParcel = b.on_parcel === true;
    if (b.gers_id) seen.add(b.gers_id);
    features.push({
      type: 'Feature',
      properties: {
        location: onParcel ? 'parcel' : 'crossing',
        on_parcel: onParcel,
        crosses_boundary: Boolean(b.crosses_boundary),
        area_sqft: Number.isFinite(Number(b.area_sqft)) ? Number(b.area_sqft) : null,
        height_m: Number.isFinite(Number(b.height_m)) ? Number(b.height_m) : null,
        share_inside: Number.isFinite(Number(b.share_inside)) ? Number(b.share_inside) : null,
        gers_id: b.gers_id || null,
      },
      geometry: b.geometry,
    });
  });
  const gj = bf.geojson;
  if (gj && gj.type === 'FeatureCollection' && Array.isArray(gj.features)) {
    gj.features.forEach((f) => {
      if (!isPolygonFeature(f)) return;
      const p = f.properties || {};
      const location = p.location || 'surroundings';
      if ((location === 'parcel' || location === 'crossing') && (!p.gers_id || seen.has(p.gers_id))) return;
      features.push({
        type: 'Feature',
        properties: {
          location,
          on_parcel: location === 'parcel',
          crosses_boundary: location === 'crossing',
          area_sqft: Number.isFinite(Number(p.area_sqft)) ? Number(p.area_sqft) : null,
          height_m: Number.isFinite(Number(p.height_m)) ? Number(p.height_m) : null,
          gers_id: p.gers_id || null,
        },
        geometry: f.geometry,
      });
    });
  }
  return features;
}

// Number(null) is 0: a missing value must stay missing, not read as zero.
const finiteOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const isLineFeature = (f) => f && f.geometry && Array.isArray(f.geometry.coordinates) && (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString');

/**
 * Flood features the layer will draw: zone polygons of the mapped classes
 * (floodway, 1%, 0.2%) tagged `parcel` or `surroundings`, plus the base flood
 * elevation lines. [] when there is nothing to draw (no GeoJSON, an older
 * backend, or a lot entirely in minimal-hazard zone X with no radius).
 */
export function floodFeatures(geojson) {
  if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) return [];
  const out = [];
  geojson.features.forEach((f) => {
    const p = (f && f.properties) || {};
    const location = p.location === 'surroundings' ? 'surroundings' : 'parcel';
    if (p.kind === 'bfe_line') {
      if (!isLineFeature(f)) return;
      out.push({ type: 'Feature', properties: { kind: 'bfe_line', location, elev_ft: finiteOrNull(p.elev_ft), datum: p.datum || null }, geometry: f.geometry });
      return;
    }
    if (!isPolygonFeature(f) || !FLOOD_MAP_CLASSES.includes(p.class)) return;
    out.push({
      type: 'Feature',
      properties: {
        kind: 'zone',
        location,
        class: p.class,
        label: p.label || null,
        share: finiteOrNull(p.share),
        acres: finiteOrNull(p.acres),
        static_bfe_ft: finiteOrNull(p.static_bfe_ft),
        share_of_ring: finiteOrNull(p.share_of_ring),
      },
      geometry: f.geometry,
    });
  });
  return out;
}

/** Hover text for a flood zone polygon. */
export function floodZoneLabel(props) {
  const p = props || {};
  const what = FLOOD_CLASS_TEXT[p.class] || 'Flood zone';
  if (p.location === 'surroundings') return `${what} · around the lot`;
  const share = finiteOrNull(p.share);
  const acres = finiteOrNull(p.acres);
  const bfe = finiteOrNull(p.static_bfe_ft);
  return [
    p.label ? `Zone ${p.label}` : null,
    what,
    share !== null ? `${Math.round(share * 1000) / 10}% of the lot${acres !== null ? ` (${acres} ac)` : ''}` : null,
    bfe !== null ? `BFE ${bfe} ft` : null,
  ].filter(Boolean).join(' · ');
}

/** First road/label layer of the base style — topo layers slot underneath it. */
function firstLineOrSymbolLayerId(map) {
  try {
    const layers = map.getStyle()?.layers || [];
    const hit = layers.find((l) => l.type === 'line' || l.type === 'symbol');
    return hit ? hit.id : undefined;
  } catch (e) {
    return undefined;
  }
}

/**
 * Mapbox map for the Land Evaluation tool: satellite imagery with
 * topography (hillshade, contour lines, optional 3D terrain), the subject
 * marker, the demographics radius ring, optional employment radii, and a
 * pin-drop mode that evaluates an arbitrary coordinate.
 */
const LandEvaluationMap = forwardRef(function LandEvaluationMap({
  center,
  radiusMiles,
  address,
  previewCenter,
  pinMode,
  onPinModeChange,
  onPinDrop,
  showJobRadii,
  onToggleJobRadii,
  mapStyle,
  onMapStyleChange,
  places,
  placesRadiusMiles,
  showAnchors = true,
  onToggleAnchors,
  showExternalities = true,
  onToggleExternalities,
  showPublicPlaces = true,
  focusPlace,
  parcel,
  zoneBoundary,
  showParcel = true,
  showZone = false,
  showContours = true,
  terrain3d = false,
  terrainExaggeration = 1,
  onZoomChange,
  terrain,
  showTerrain = true,
  showParcelContours = true,
  terrainMode = 'elevation',
  maxSlopePercent = DEFAULT_MAX_SLOPE_PERCENT,
  comps,
  showComps = true,
  treeCanopy,
  showTreeCanopy = true,
  buildingFootprints,
  showFootprints = true,
  showNeighbouringFootprints = true,
  flood,
  showFlood = true,
}, ref) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const pinStateRef = useRef({ subject: null, preview: null, label: '' });
  const styleReadyRef = useRef(false);
  const styleRef = useRef(mapStyle);
  const lastCenterKeyRef = useRef(null);
  const zoomCallbackRef = useRef(onZoomChange);
  zoomCallbackRef.current = onZoomChange;
  const propsRef = useRef({});
  const popupRef = useRef(null);
  propsRef.current = { center, radiusMiles, address, showJobRadii, pinMode, onPinDrop, onPinModeChange, mapStyle, showContours, terrain3d, places, placesRadiusMiles, showAnchors, showExternalities, showPublicPlaces, parcel, zoneBoundary, showParcel, showZone, terrain, showTerrain, showParcelContours, terrainMode, maxSlopePercent, terrainExaggeration, comps, showComps, treeCanopy, showTreeCanopy, buildingFootprints, showFootprints, showNeighbouringFootprints, flood, showFlood };

  // Hillshade + contours (+ 3D terrain) on top of whichever base style is active.
  const syncTopo = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { mapStyle: style, showContours: contours, terrain3d: threeD, terrainExaggeration: exag } = propsRef.current;
    const onSatellite = style !== 'streets';

    if (!map.getSource(DEM_SOURCE)) {
      map.addSource(DEM_SOURCE, { type: 'raster-dem', url: DEM_TILESET, tileSize: 512, maxzoom: 14 });
    }
    if (!map.getSource(TERRAIN_DEM_SOURCE)) {
      map.addSource(TERRAIN_DEM_SOURCE, { type: 'raster-dem', url: DEM_TILESET, tileSize: 512, maxzoom: 14 });
    }
    const usgsTiles = terrainTileUrlTemplate();
    if (usgsTiles && !map.getSource(USGS_DEM_SOURCE)) {
      map.addSource(USGS_DEM_SOURCE, { type: 'raster-dem', tiles: [usgsTiles], tileSize: 512, minzoom: USGS_DEM_MIN_ZOOM, maxzoom: USGS_DEM_MAX_ZOOM, encoding: 'mapbox', bounds: [-180, 17, -64, 72] });
    }
    // 3D buildings from the style's streets source (footprints carry heights);
    // in flat country this is what makes the tilted view read as 3D.
    if (!map.getLayer(BUILDINGS_LAYER) && map.getSource('composite')) {
      map.addLayer({
        id: BUILDINGS_LAYER,
        type: 'fill-extrusion',
        source: 'composite',
        'source-layer': 'building',
        minzoom: BUILDINGS_MIN_ZOOM,
        filter: ['==', ['get', 'extrude'], 'true'],
        layout: { visibility: 'none' },
        paint: {
          'fill-extrusion-color': onSatellite ? '#e5e7eb' : '#d1cfc7',
          'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], BUILDINGS_MIN_ZOOM, 0, BUILDINGS_MIN_ZOOM + 0.6, ['get', 'height']],
          'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], BUILDINGS_MIN_ZOOM, 0, BUILDINGS_MIN_ZOOM + 0.6, ['get', 'min_height']],
          'fill-extrusion-opacity': 0.82,
          'fill-extrusion-vertical-gradient': true,
        },
      }, firstLineOrSymbolLayerId(map));
    }
    if (!map.getSource(CONTOUR_SOURCE)) {
      map.addSource(CONTOUR_SOURCE, { type: 'vector', url: TERRAIN_TILESET });
    }

    if (!map.getLayer('le-hillshade')) {
      const beforeId = firstLineOrSymbolLayerId(map);
      if (map.getSource(USGS_DEM_SOURCE)) {
        map.addLayer({
          id: 'le-hillshade-usgs',
          type: 'hillshade',
          source: USGS_DEM_SOURCE,
          minzoom: USGS_DEM_MIN_ZOOM,
          paint: {
            'hillshade-exaggeration': onSatellite ? 0.55 : 0.4,
            'hillshade-illumination-direction': 335,
            'hillshade-shadow-color': onSatellite ? '#0b1220' : '#5b4a3a',
            'hillshade-highlight-color': '#ffffff',
            'hillshade-accent-color': onSatellite ? '#0b1220' : '#7a6a58',
          },
        }, beforeId);
      }
      map.addLayer({
        id: 'le-hillshade',
        type: 'hillshade',
        source: DEM_SOURCE,
        maxzoom: map.getSource(USGS_DEM_SOURCE) ? USGS_DEM_MIN_ZOOM : 24,
        paint: {
          'hillshade-exaggeration': onSatellite ? 0.45 : 0.3,
          'hillshade-illumination-direction': 335,
          'hillshade-shadow-color': onSatellite ? '#0b1220' : '#5b4a3a',
          'hillshade-highlight-color': '#ffffff',
          'hillshade-accent-color': onSatellite ? '#0b1220' : '#7a6a58',
        },
      }, beforeId);

      const minorFilter = ['all', ['!=', ['get', 'index'], -1], ['!', ['in', ['get', 'index'], ['literal', [5, 10]]]]];
      const indexFilter = ['in', ['get', 'index'], ['literal', [5, 10]]];
      const lineColor = onSatellite ? '#ffffff' : '#8f5f33';
      map.addLayer({
        id: 'le-contour-minor',
        type: 'line',
        source: CONTOUR_SOURCE,
        'source-layer': 'contour',
        filter: minorFilter,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': lineColor,
          'line-opacity': onSatellite ? 0.42 : 0.5,
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.4, 14, 0.7, 17, 1.1],
        },
      }, beforeId);
      map.addLayer({
        id: 'le-contour-index',
        type: 'line',
        source: CONTOUR_SOURCE,
        'source-layer': 'contour',
        filter: indexFilter,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': lineColor,
          'line-opacity': onSatellite ? 0.75 : 0.8,
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.9, 14, 1.4, 17, 2],
        },
      }, beforeId);
      map.addLayer({
        id: 'le-contour-label',
        type: 'symbol',
        source: CONTOUR_SOURCE,
        'source-layer': 'contour',
        filter: indexFilter,
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 320,
          'text-field': ['concat', ['to-string', ['round', ['*', ['get', 'ele'], FEET_PER_METER]]], ' ft'],
          'text-size': 10,
          'text-max-angle': 25,
          'text-rotation-alignment': 'map',
          'text-pitch-alignment': 'viewport',
          'text-letter-spacing': 0.05,
        },
        paint: {
          'text-color': onSatellite ? '#ffffff' : '#6b4423',
          'text-halo-color': onSatellite ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.9)',
          'text-halo-width': 1.2,
        },
      }, beforeId);
    }

    const visibility = contours ? 'visible' : 'none';
    ['le-contour-minor', 'le-contour-index', 'le-contour-label'].forEach((id) => {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility);
    });

    if (map.getLayer(BUILDINGS_LAYER)) {
      map.setLayoutProperty(BUILDINGS_LAYER, 'visibility', threeD ? 'visible' : 'none');
    }
    if (typeof map.setTerrain === 'function') {
      // True-scale 3D from USGS 1 m data when the proxy is available; the
      // exaggeration is whatever the user chose (x1 = true scale).
      const source = map.getSource(USGS_DEM_SOURCE) ? USGS_DEM_SOURCE : TERRAIN_DEM_SOURCE;
      map.setTerrain(threeD ? { source, exaggeration: Number(exag) > 0 ? Number(exag) : 1 } : null);
    }
    if (typeof map.setFog === 'function') {
      map.setFog(threeD ? { range: [1, 12], 'horizon-blend': 0.08, color: '#dfe6ee', 'high-color': '#a9c3e0', 'space-color': '#0b1220', 'star-intensity': 0 } : null);
    }
  }, []);

  // Parcel outline from the assessor record and, optionally, the zoning district polygon.
  const syncParcel = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { parcel: parcelGeom, zoneBoundary: zoneGeom, showParcel: parcelOn, showZone: zoneOn } = propsRef.current;
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const removeSource = (id) => { if (map.getSource(id)) map.removeSource(id); };
    const feature = (geometry) => ({ type: 'Feature', properties: {}, geometry });
    if (!zoneGeom) {
      ['le-zone-fill', 'le-zone-line'].forEach(removeLayer); removeSource('le-zone');
    } else if (map.getSource('le-zone')) {
      map.getSource('le-zone').setData(feature(zoneGeom));
    } else {
      map.addSource('le-zone', { type: 'geojson', data: feature(zoneGeom) });
      map.addLayer({ id: 'le-zone-fill', type: 'fill', source: 'le-zone', paint: { 'fill-color': ZONE_COLOR, 'fill-opacity': ZONE_FILL_OPACITY } });
      map.addLayer({ id: 'le-zone-line', type: 'line', source: 'le-zone', paint: { 'line-color': ZONE_COLOR, 'line-width': ZONE_LINE_WIDTH, 'line-dasharray': ZONE_DASH, 'line-opacity': ZONE_LINE_OPACITY } });
    }
    if (!parcelGeom) {
      ['le-parcel-fill', 'le-parcel-casing', 'le-parcel-line'].forEach(removeLayer); removeSource('le-parcel');
    } else if (map.getSource('le-parcel')) {
      map.getSource('le-parcel').setData(feature(parcelGeom));
    } else {
      map.addSource('le-parcel', { type: 'geojson', data: feature(parcelGeom) });
      map.addLayer({ id: 'le-parcel-fill', type: 'fill', source: 'le-parcel', paint: { 'fill-color': PARCEL_COLOR, 'fill-opacity': PARCEL_FILL_OPACITY } });
      map.addLayer({ id: 'le-parcel-casing', type: 'line', source: 'le-parcel', layout: { 'line-join': 'round' }, paint: { 'line-color': PARCEL_CASING_COLOR, 'line-width': PARCEL_CASING_WIDTH, 'line-opacity': PARCEL_CASING_OPACITY } });
      map.addLayer({ id: 'le-parcel-line', type: 'line', source: 'le-parcel', layout: { 'line-join': 'round' }, paint: { 'line-color': PARCEL_COLOR, 'line-width': PARCEL_LINE_WIDTH } });
    }
    ['le-zone-fill', 'le-zone-line'].forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', zoneOn ? 'visible' : 'none'); });
    ['le-parcel-fill', 'le-parcel-casing', 'le-parcel-line'].forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', parcelOn ? 'visible' : 'none'); });
  }, []);

  // Parcel terrain: one square cell per USGS sample, colored by elevation,
  // plus the low and high points.
  const syncTerrain = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { terrain: t, showTerrain: on, showParcelContours: contoursOn, terrainMode, maxSlopePercent } = propsRef.current;
    const slopeMode = terrainMode === 'slope';
    const maxSlope = Number(maxSlopePercent) || DEFAULT_MAX_SLOPE_PERCENT;
    const steepFilter = ['>', ['get', 's'], maxSlope];
    // Slope view paints every cell over the max solid red so the mark is
    // unmistakable even where the band color is already red.
    const fillColor = slopeMode ? ['case', ['>', ['get', 's'], maxSlope], STEEP_COLOR, ['get', 'sc']] : ['get', 'c'];
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const removeSource = (id) => { if (map.getSource(id)) map.removeSource(id); };
    const LAYERS = ['le-terrain-fill', ...STEEP_LAYERS, ...CONTOUR_LAYERS, 'le-terrain-extremes-circle', 'le-terrain-extremes-label'];
    const samples = Array.isArray(t?.samples) ? t.samples : [];
    if (!samples.length) {
      LAYERS.forEach(removeLayer);
      ['le-terrain-cells', 'le-terrain-extremes', 'le-terrain-contours'].forEach(removeSource);
      return;
    }
    const min = Number(t.stats?.min_m); const max = Number(t.stats?.max_m);
    const lo = Number.isFinite(min) ? min : 0;
    const span = Number.isFinite(min) && Number.isFinite(max) && max > min ? max - min : 1;

    // Cells sized from the grid's real row/column pitch, colored by elevation
    // and darkened by the server's hillshade so the surface reads in 2D.
    const spacing = Number(t.footprint?.spacing_m) > 0 ? Number(t.footprint.spacing_m) : 2;
    const lat0 = samples[0][1];
    const pitch = (values, fallbackDeg) => {
      const uniq = Array.from(new Set(values.map((v) => Math.round(v * 1e7) / 1e7))).sort((a, b) => a - b);
      const gaps = uniq.slice(1).map((v, i) => v - uniq[i]).filter((g) => g > 1e-9).sort((a, b) => a - b);
      return gaps.length ? gaps[Math.floor(gaps.length / 2)] : fallbackDeg;
    };
    const dLat = pitch(samples.map((s) => s[1]), spacing / 110540) / 2;
    const dLng = pitch(samples.map((s) => s[0]), spacing / (111320 * Math.cos((lat0 * Math.PI) / 180))) / 2;
    const cells = {
      type: 'FeatureCollection',
      features: samples.map(([lng, lat, e, shade, slope]) => ({
        type: 'Feature',
        // e: elevation, c: elevation color, s: slope percent (-1 unknown), sc: slope band color
        properties: { e, s: Number.isFinite(slope) ? slope : -1, c: shadedElevationColor((e - lo) / span, shade), sc: shadedSlopeColor(slope, shade) },
        geometry: { type: 'Polygon', coordinates: [[[lng - dLng, lat - dLat], [lng + dLng, lat - dLat], [lng + dLng, lat + dLat], [lng - dLng, lat + dLat], [lng - dLng, lat - dLat]]] },
      })),
    };
    // Parcel contours: smoothed lines from the server, ranked from the high
    // point (rank 0) downwards so the sweep can run high → low.
    const levels = Array.isArray(t.contours?.levels) ? t.contours.levels.slice().sort((a, b) => b.elevation_ft - a.elevation_ft) : [];
    const contours = {
      type: 'FeatureCollection',
      features: levels.flatMap((lvl, rank) => (lvl.lines || []).map((coords) => ({
        type: 'Feature',
        properties: { ft: Math.round(lvl.elevation_ft * 10) / 10, rank, index: !!lvl.index, label: `${Math.round(lvl.elevation_ft * 10) / 10} ft` },
        geometry: { type: 'LineString', coordinates: coords },
      }))),
    };
    const ex = t.extremes || {};
    const extremes = {
      type: 'FeatureCollection',
      features: ['low', 'high'].filter((k) => ex[k] && Number.isFinite(ex[k].longitude)).map((k) => ({
        type: 'Feature',
        properties: { kind: k, label: `${k === 'low' ? '▼ Low' : '▲ High'} ${Math.round(ex[k].elevation_ft * 10) / 10} ft` },
        geometry: { type: 'Point', coordinates: [ex[k].longitude, ex[k].latitude] },
      })),
    };
    const beforeId = map.getLayer('le-parcel-casing') ? 'le-parcel-casing' : undefined;
    if (map.getSource('le-terrain-cells')) {
      map.getSource('le-terrain-cells').setData(cells);
      map.getSource('le-terrain-extremes').setData(extremes);
      map.getSource('le-terrain-contours').setData(contours);
    } else {
      map.addSource('le-terrain-cells', { type: 'geojson', data: cells });
      map.addSource('le-terrain-extremes', { type: 'geojson', data: extremes });
      map.addSource('le-terrain-contours', { type: 'geojson', data: contours });
      map.addLayer({ id: 'le-terrain-fill', type: 'fill', source: 'le-terrain-cells', paint: { 'fill-color': fillColor, 'fill-opacity': 0.7, 'fill-antialias': false } }, beforeId);
      // Cells steeper than the chosen max slope get a diagonal white hatch over
      // their solid red so they read at any zoom.
      ensureHatchImage(map);
      map.addLayer({ id: 'le-terrain-steep-hatch', type: 'fill', source: 'le-terrain-cells', filter: steepFilter, paint: { 'fill-pattern': HATCH_IMAGE, 'fill-opacity': 0.8, 'fill-antialias': false } }, beforeId);
      // Topographic contours: dark casing, white line, an amber glow layer the
      // sweep animates, and elevation labels along the index contours.
      map.addLayer({ id: 'le-terrain-contour-casing', type: 'line', source: 'le-terrain-contours', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#0b1220', 'line-width': ['case', ['get', 'index'], 3.4, 2.4], 'line-opacity': 0.32 } }, beforeId);
      map.addLayer({ id: 'le-terrain-contour-line', type: 'line', source: 'le-terrain-contours', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': ['case', ['get', 'index'], 1.7, 1.0], 'line-opacity': 0.92 } }, beforeId);
      map.addLayer({ id: 'le-terrain-contour-glow', type: 'line', source: 'le-terrain-contours', layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#fde68a', 'line-width': 1.2, 'line-opacity': 0, 'line-blur': 0.5 } }, beforeId);
      map.addLayer({ id: 'le-terrain-contour-label', type: 'symbol', source: 'le-terrain-contours', filter: ['==', ['get', 'index'], true], layout: { 'symbol-placement': 'line', 'symbol-spacing': 240, 'text-field': ['get', 'label'], 'text-size': 10.5, 'text-max-angle': 30, 'text-padding': 4 }, paint: { 'text-color': '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 } }, beforeId);
      map.addLayer({ id: 'le-terrain-extremes-circle', type: 'circle', source: 'le-terrain-extremes', paint: { 'circle-radius': 6, 'circle-color': ['match', ['get', 'kind'], 'low', TERRAIN_LOW_COLOR, TERRAIN_HIGH_COLOR], 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 } });
      map.addLayer({ id: 'le-terrain-extremes-label', type: 'symbol', source: 'le-terrain-extremes', layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-offset': [0, -1.2], 'text-anchor': 'bottom', 'text-allow-overlap': true }, paint: { 'text-color': '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 } });
    }
    if (map.getLayer('le-terrain-fill')) map.setPaintProperty('le-terrain-fill', 'fill-color', fillColor);
    STEEP_LAYERS.forEach((id) => { if (map.getLayer(id)) { map.setFilter(id, steepFilter); map.setLayoutProperty(id, 'visibility', on && slopeMode ? 'visible' : 'none'); } });
    ['le-terrain-fill', 'le-terrain-extremes-circle', 'le-terrain-extremes-label'].forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); });
    CONTOUR_LAYERS.forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on && contoursOn ? 'visible' : 'none'); });
  }, []);

  // Sale comps: one dot per listing — new construction in the accent hue,
  // existing in blue; active filled, inactive hollow.
  const syncComps = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { comps: c, showComps: on } = propsRef.current;
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const points = Array.isArray(c?.points) ? c.points.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)) : [];
    if (!points.length) {
      ['le-comps-inactive', 'le-comps-active'].forEach(removeLayer);
      if (map.getSource('le-comps')) map.removeSource('le-comps');
      return;
    }
    const fc = {
      type: 'FeatureCollection',
      features: points.map((p) => ({
        type: 'Feature',
        properties: {
          status: p.status,
          isNew: p.cohort === 'new_explicit' || p.cohort === 'new_inferred',
          label: `${p.status === 'active' ? 'Asking' : 'Last asking'} ${p.asking_price || p.last_known_asking_price ? `$${Math.round((p.asking_price || p.last_known_asking_price) / 1000)}K` : '—'}${p.price_per_sqft ? ` · $${Math.round(p.price_per_sqft)}/sf` : ''}${p.living_area_sqft ? ` · ${Math.round(p.living_area_sqft).toLocaleString()} sf` : ''}${p.year_built ? ` · ${p.year_built}` : ''}${p.cohort === 'new_explicit' ? ' · NEW' : ''}\n${p.address || ''}`,
        },
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      })),
    };
    if (map.getSource('le-comps')) {
      map.getSource('le-comps').setData(fc);
    } else {
      map.addSource('le-comps', { type: 'geojson', data: fc });
      const colorExpr = ['case', ['get', 'isNew'], COMP_NEW_COLOR, COMP_EXISTING_COLOR];   // violet = new construction, blue = existing (green is the anchors')
      map.addLayer({ id: 'le-comps-inactive', type: 'circle', source: 'le-comps', filter: ['==', ['get', 'status'], 'inactive'], paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 15, 5.5], 'circle-color': '#ffffff', 'circle-opacity': 0.6, 'circle-stroke-color': colorExpr, 'circle-stroke-width': 1.6 } });
      map.addLayer({ id: 'le-comps-active', type: 'circle', source: 'le-comps', filter: ['==', ['get', 'status'], 'active'], paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 15, 7], 'circle-color': colorExpr, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 1.5 } });
      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10, className: 'le-popup' });
      ['le-comps-active', 'le-comps-inactive'].forEach((id) => {
        map.on('mouseenter', id, (e) => { map.getCanvas().style.cursor = 'pointer'; const f = e.features && e.features[0]; if (f) popup.setLngLat(f.geometry.coordinates).setText(f.properties.label).addTo(map); });
        map.on('mouseleave', id, () => { map.getCanvas().style.cursor = propsRef.current.pinMode ? 'crosshair' : ''; popup.remove(); });
      });
    }
    ['le-comps-inactive', 'le-comps-active'].forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); });
  }, []);

  // Tree canopy: fill polygons from the 1 m canopy-height model. Parcel patches
  // solid, surroundings faded, a thin line for the dataset's parcel boundary and
  // a dashed faint circle for the search radius. Slots under the parcel casing
  // so the assessor outline stays on top.
  const syncCanopy = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { treeCanopy: canopy, showTreeCanopy: on } = propsRef.current;
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const features = canopyFeatures(canopy);
    if (!features.length) {
      CANOPY_LAYERS.forEach(removeLayer);
      if (map.getSource(CANOPY_SOURCE)) map.removeSource(CANOPY_SOURCE);
      return;
    }
    const fc = {
      type: 'FeatureCollection',
      features: features.map((f) => {
        const p = f.properties || {};
        const location = p.location || 'surroundings';
        const area = Number(p.area_sqft);
        const maxH = Number(p.max_height_m);
        return {
          type: 'Feature',
          properties: {
            ...p,
            location,
            label: `Canopy · ${Number.isFinite(area) ? `${Math.round(area).toLocaleString()} sqft` : 'area unknown'} · max ${Number.isFinite(maxH) ? `${Math.round(maxH * 10) / 10} m` : '—'}`,
          },
          geometry: f.geometry,
        };
      }),
    };
    const patchFilter = ['in', ['get', 'location'], ['literal', ['parcel', 'surroundings']]];
    if (map.getSource(CANOPY_SOURCE)) {
      map.getSource(CANOPY_SOURCE).setData(fc);
    } else {
      const beforeId = map.getLayer('le-parcel-casing') ? 'le-parcel-casing' : undefined;
      map.addSource(CANOPY_SOURCE, { type: 'geojson', data: fc });
      map.addLayer({
        id: 'le-canopy-fill', type: 'fill', source: CANOPY_SOURCE, filter: patchFilter,
        paint: { 'fill-color': CANOPY_COLOR, 'fill-opacity': ['match', ['get', 'location'], 'parcel', CANOPY_PARCEL_OPACITY, CANOPY_SURROUND_OPACITY] },
      }, beforeId);
      map.addLayer({
        id: 'le-canopy-patch-line', type: 'line', source: CANOPY_SOURCE, filter: patchFilter,
        paint: { 'line-color': CANOPY_COLOR, 'line-width': 0.8, 'line-opacity': ['match', ['get', 'location'], 'parcel', 0.85, 0.4] },
      }, beforeId);
      map.addLayer({
        id: 'le-canopy-boundary-line', type: 'line', source: CANOPY_SOURCE, filter: ['==', ['get', 'location'], 'parcel_boundary'],
        layout: { 'line-join': 'round' },
        paint: { 'line-color': CANOPY_COLOR, 'line-width': 1.2, 'line-opacity': 0.9 },
      }, beforeId);
      map.addLayer({
        id: 'le-canopy-radius-line', type: 'line', source: CANOPY_SOURCE, filter: ['==', ['get', 'location'], 'search_radius'],
        paint: { 'line-color': CANOPY_COLOR, 'line-width': 1, 'line-dasharray': [2, 2], 'line-opacity': 0.45 },
      }, beforeId);
    }
    CANOPY_LAYERS.forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); });
  }, []);

  // Building footprints: the API's Overture outlines. On-parcel solid slate,
  // neighbours' structures that cross the line hatched, surroundings (only when
  // the neighbouring geometry was requested) faint, a dashed ring for the search
  // radius and a thin line for the boundary the dataset actually used (which is
  // the 60 m box when there was no parcel polygon). Slots under the parcel
  // casing; the basemap's 3D extrusions are left alone.
  const syncFootprints = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { buildingFootprints: bf, showFootprints: on, showNeighbouringFootprints: surroundOn } = propsRef.current;
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const features = footprintFeatures(bf);
    if (!features.length) {
      FOOTPRINT_LAYERS.forEach(removeLayer);
      if (map.getSource(FOOTPRINT_SOURCE)) map.removeSource(FOOTPRINT_SOURCE);
      return;
    }
    const fc = {
      type: 'FeatureCollection',
      features: features.map((f) => {
        const p = f.properties;
        const where = p.location === 'parcel' ? 'On parcel' : p.location === 'crossing' ? 'Crosses the boundary · neighbouring structure' : 'Surroundings';
        return {
          ...f,
          properties: {
            ...p,
            label: `Structure · ${Number.isFinite(p.area_sqft) ? `${Math.round(p.area_sqft).toLocaleString()} sqft` : 'area unknown'} · ${Number.isFinite(p.height_m) ? `${Math.round(p.height_m * 10) / 10} m` : 'height unknown'} · ${where}`,
          },
        };
      }),
    };
    const lotFilter = ['in', ['get', 'location'], ['literal', ['parcel', 'crossing']]];
    if (map.getSource(FOOTPRINT_SOURCE)) {
      map.getSource(FOOTPRINT_SOURCE).setData(fc);
    } else {
      const beforeId = map.getLayer('le-parcel-casing') ? 'le-parcel-casing' : undefined;
      map.addSource(FOOTPRINT_SOURCE, { type: 'geojson', data: fc });
      ensureHatchImage(map, FOOTPRINT_HATCH_IMAGE, [53, 80, 107], 190);
      map.addLayer({
        id: 'le-footprints-surround-fill', type: 'fill', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'surroundings'],
        paint: { 'fill-color': FOOTPRINT_COLOR, 'fill-opacity': FOOTPRINT_SURROUND_OPACITY },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-surround-line', type: 'line', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'surroundings'],
        paint: { 'line-color': FOOTPRINT_COLOR, 'line-width': 0.8, 'line-opacity': 0.45 },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-fill', type: 'fill', source: FOOTPRINT_SOURCE, filter: lotFilter,
        paint: { 'fill-color': FOOTPRINT_COLOR, 'fill-opacity': ['match', ['get', 'location'], 'parcel', FOOTPRINT_PARCEL_OPACITY, FOOTPRINT_CROSSING_OPACITY] },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-crossing-hatch', type: 'fill', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'crossing'],
        paint: { 'fill-pattern': FOOTPRINT_HATCH_IMAGE, 'fill-opacity': 0.7 },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-line', type: 'line', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'parcel'],
        layout: { 'line-join': 'round' },
        paint: { 'line-color': FOOTPRINT_COLOR, 'line-width': 1.5, 'line-opacity': 0.95 },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-crossing-line', type: 'line', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'crossing'],
        layout: { 'line-join': 'round' },
        paint: { 'line-color': FOOTPRINT_COLOR, 'line-width': 1.5, 'line-dasharray': [2, 1.5], 'line-opacity': 0.85 },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-boundary-line', type: 'line', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'parcel_boundary'],
        layout: { 'line-join': 'round' },
        paint: { 'line-color': FOOTPRINT_COLOR, 'line-width': 1, 'line-opacity': 0.6 },
      }, beforeId);
      map.addLayer({
        id: 'le-footprints-radius-line', type: 'line', source: FOOTPRINT_SOURCE, filter: ['==', ['get', 'location'], 'search_radius'],
        paint: { 'line-color': FOOTPRINT_COLOR, 'line-width': 1, 'line-dasharray': [2, 2], 'line-opacity': 0.5 },
      }, beforeId);
    }
    FOOTPRINT_PARCEL_LAYERS.forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); });
    FOOTPRINT_SURROUND_LAYERS.forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on && surroundOn ? 'visible' : 'none'); });
  }, []);

  // Flood zones: fills by class (darker = more restricted), a thin outline, and
  // dashed base-flood-elevation lines labelled with their elevation. Re-slotted
  // on every sync because the terrain grid re-adds its layers under the parcel.
  const syncFlood = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { flood: geojson, showFlood: on } = propsRef.current;
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const features = floodFeatures(geojson);
    if (!features.length) {
      FLOOD_LAYERS.forEach(removeLayer);
      if (map.getSource(FLOOD_SOURCE)) map.removeSource(FLOOD_SOURCE);
      return;
    }
    const fc = {
      type: 'FeatureCollection',
      features: features.map((f) => (f.properties.kind === 'zone'
        ? { ...f, properties: { ...f.properties, hover: floodZoneLabel(f.properties) } }
        : { ...f, properties: { ...f.properties, text: f.properties.elev_ft !== null ? `BFE ${f.properties.elev_ft} ft${f.properties.datum ? ` ${f.properties.datum}` : ''}` : 'BFE' } })),
    };
    const byClass = (key) => ['match', ['get', 'class'], ...FLOOD_MAP_CLASSES.flatMap((c) => [c, FLOOD_COLORS[c][key]]), FLOOD_COLORS.moderate[key]];
    const fade = FLOOD_COLORS.surround_opacity_scale;
    const zoneFilter = ['==', ['get', 'kind'], 'zone'];
    const bfeFilter = ['==', ['get', 'kind'], 'bfe_line'];
    if (map.getSource(FLOOD_SOURCE)) {
      map.getSource(FLOOD_SOURCE).setData(fc);
    } else {
      map.addSource(FLOOD_SOURCE, { type: 'geojson', data: fc });
      map.addLayer({
        id: 'le-flood-fill', type: 'fill', source: FLOOD_SOURCE, filter: zoneFilter,
        paint: { 'fill-color': byClass('fill'), 'fill-opacity': ['*', byClass('fill_opacity'), ['match', ['get', 'location'], 'surroundings', fade, 1]] },
      });
      map.addLayer({
        id: 'le-flood-line', type: 'line', source: FLOOD_SOURCE, filter: zoneFilter,
        layout: { 'line-join': 'round' },
        paint: { 'line-color': byClass('line'), 'line-width': byClass('line_width'), 'line-opacity': ['match', ['get', 'location'], 'surroundings', 0.5, 0.9] },
      });
      map.addLayer({
        id: 'le-flood-bfe-line', type: 'line', source: FLOOD_SOURCE, filter: bfeFilter,
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': FLOOD_COLORS.bfe.line, 'line-width': FLOOD_COLORS.bfe.width, 'line-dasharray': FLOOD_COLORS.bfe.dash },
      });
      map.addLayer({
        id: 'le-flood-bfe-label', type: 'symbol', source: FLOOD_SOURCE, filter: bfeFilter,
        layout: { 'symbol-placement': 'line', 'symbol-spacing': 260, 'text-field': ['get', 'text'], 'text-size': 10.5, 'text-max-angle': 30, 'text-padding': 4 },
        paint: { 'text-color': FLOOD_COLORS.bfe.line, 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
      });
    }
    const beforeId = FLOOD_BEFORE_CANDIDATES.find((id) => map.getLayer(id));
    ['le-flood-fill', 'le-flood-line'].forEach((id) => { if (map.getLayer(id)) map.moveLayer(id, beforeId); });
    FLOOD_LAYERS.forEach((id) => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none'); });
  }, []);

  // Nearby places (`places` is the selectPlacesLayers() view, never a raw
  // document): commercial anchors (green, sized by score), public places
  // (blue-teal, uniform size — there is no score), adverse uses (red, sized
  // by decayed impact) and the places radius ring.
  const syncPlaces = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { places: data, center: c, placesRadiusMiles: pr, showAnchors: anchorsOn, showExternalities: extOn, showPublicPlaces: publicOn } = propsRef.current;
    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const removeSource = (id) => { if (map.getSource(id)) map.removeSource(id); };
    if (!data || !data.hasAny || !c) {
      PLACES_LAYERS.forEach(removeLayer);
      ['le-places-ring', 'le-anchors', 'le-public-places', 'le-externalities'].forEach(removeSource);
      return;
    }
    const publicPlaces = {
      type: 'FeatureCollection',
      features: (data.publicPlaces || []).filter((p) => Number.isFinite(p.longitude) && Number.isFinite(p.latitude)).map((p, idx) => ({
        type: 'Feature',
        properties: {
          kind: 'public', name: p.name, category: p.category, categoryLabel: publicCategoryLabel(p.category), distance: p.distance_miles,
          locality: p.locality, refreshed: p.date_refreshed, stale: Boolean(p.is_stale),
          labelled: idx < PUBLIC_PLACE_LABELLED ? 1 : 0,
        },
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      })),
    };
    const ring = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: circleCoordinates([c.longitude, c.latitude], (pr || 3) * MILE_METERS) },
    };
    const anchors = {
      type: 'FeatureCollection',
      features: (data.anchors || []).filter((a) => a.nearest && Number.isFinite(a.nearest.longitude) && Number.isFinite(a.nearest.latitude)).map((a) => ({
        type: 'Feature',
        properties: {
          kind: 'anchor', name: a.name, tier: a.tier, score: a.anchor_score || 0, group: a.group, subtype: a.subtype,
          distance: a.distance_miles, refreshed: a.nearest.date_refreshed, stale: Boolean(a.nearest.is_stale), locations: a.locations_within_radius,
          labelled: a.tier === 'A+' || a.tier === 'A' ? 1 : 0,
        },
        geometry: { type: 'Point', coordinates: [a.nearest.longitude, a.nearest.latitude] },
      })),
    };
    const externalities = {
      type: 'FeatureCollection',
      features: (data.externalities || []).filter((e) => Number.isFinite(e.longitude) && Number.isFinite(e.latitude)).map((e) => ({
        type: 'Feature',
        properties: {
          kind: 'externality', name: e.name, type: e.display_name || e.type, distance: e.distance_miles,
          base: e.base_impact_score, effective: e.effective_impact_score, magnitude: Math.abs(e.effective_impact_score || 0),
          refreshed: e.date_refreshed, stale: Boolean(e.is_stale),
        },
        geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] },
      })),
    };
    if (map.getSource('le-places-ring')) map.getSource('le-places-ring').setData(ring);
    else {
      map.addSource('le-places-ring', { type: 'geojson', data: ring });
      map.addLayer({ id: 'le-places-ring-line', type: 'line', source: 'le-places-ring', paint: { 'line-color': PLACES_RING_COLOR, 'line-width': 1.6, 'line-opacity': 0.85 } });
    }
    if (map.getSource('le-externalities')) map.getSource('le-externalities').setData(externalities);
    else {
      map.addSource('le-externalities', { type: 'geojson', data: externalities });
      map.addLayer({
        id: 'le-externalities-circle',
        type: 'circle',
        source: 'le-externalities',
        paint: {
          'circle-color': EXTERNALITY_COLOR,
          'circle-radius': ['interpolate', ['linear'], ['get', 'magnitude'], 0, 5, 40, 8, 85, 12],
          'circle-opacity': 0.9,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });
    }
    if (map.getSource('le-public-places')) map.getSource('le-public-places').setData(publicPlaces);
    else {
      map.addSource('le-public-places', { type: 'geojson', data: publicPlaces });
      map.addLayer({
        id: 'le-public-places-circle',
        type: 'circle',
        source: 'le-public-places',
        paint: {
          'circle-color': PUBLIC_PLACE_COLOR,
          'circle-radius': 5.5,
          'circle-opacity': 0.92,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });
      map.addLayer({
        id: 'le-public-places-label',
        type: 'symbol',
        source: 'le-public-places',
        filter: ['==', ['get', 'labelled'], 1],
        layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'text-offset': [0, 1.0], 'text-anchor': 'top', 'text-optional': true },
        paint: { 'text-color': PUBLIC_PLACE_LABEL_COLOR, 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
      });
    }
    if (map.getSource('le-anchors')) map.getSource('le-anchors').setData(anchors);
    else {
      map.addSource('le-anchors', { type: 'geojson', data: anchors });
      map.addLayer({
        id: 'le-anchors-circle',
        type: 'circle',
        source: 'le-anchors',
        paint: {
          'circle-color': ANCHOR_COLOR,
          'circle-radius': ['interpolate', ['linear'], ['get', 'score'], 0, 3.5, 50, 5, 80, 7.5, 100, 9.5],
          'circle-opacity': ['case', ['==', ['get', 'tier'], 'context'], 0.55, 0.92],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });
      map.addLayer({
        id: 'le-anchors-label',
        type: 'symbol',
        source: 'le-anchors',
        filter: ['==', ['get', 'labelled'], 1],
        layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'text-offset': [0, 1.1], 'text-anchor': 'top', 'text-optional': true },
        paint: { 'text-color': ANCHOR_LABEL_COLOR, 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
      });
    }
    map.setLayoutProperty('le-anchors-circle', 'visibility', anchorsOn ? 'visible' : 'none');
    map.setLayoutProperty('le-anchors-label', 'visibility', anchorsOn ? 'visible' : 'none');
    map.setLayoutProperty('le-externalities-circle', 'visibility', extOn ? 'visible' : 'none');
    map.setLayoutProperty('le-public-places-circle', 'visibility', publicOn ? 'visible' : 'none');
    map.setLayoutProperty('le-public-places-label', 'visibility', publicOn ? 'visible' : 'none');
  }, []);

  // Subject / preview pins as symbols on a GeoJSON source.
  const syncPins = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    // The features are read at apply time, not captured now: image loading is
    // asynchronous and an older sync must never overwrite a newer pin state.
    const currentData = () => {
      const { subject, preview, label } = pinStateRef.current;
      const features = [];
      if (subject) features.push({ type: 'Feature', properties: { kind: 'subject', label: label || '' }, geometry: { type: 'Point', coordinates: subject } });
      else if (preview) features.push({ type: 'Feature', properties: { kind: 'preview', label: '' }, geometry: { type: 'Point', coordinates: preview } });
      return { type: 'FeatureCollection', features };
    };
    const apply = () => {
      const live = mapRef.current;
      if (!live || live !== map || !styleReadyRef.current) return;
      const data = currentData();
      if (live.getSource(PIN_SOURCE)) {
        live.getSource(PIN_SOURCE).setData(data);
        return;
      }
      live.addSource(PIN_SOURCE, { type: 'geojson', data });
      live.addLayer({
        id: 'le-pin-halo', type: 'circle', source: PIN_SOURCE, filter: ['==', ['get', 'kind'], 'subject'],
        paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 7, 14, 14, 18, 20], 'circle-color': PIN_IMAGES.subject.fill, 'circle-opacity': 0.16, 'circle-stroke-color': PIN_IMAGES.subject.fill, 'circle-stroke-width': 1.5, 'circle-stroke-opacity': 0.45, 'circle-pitch-alignment': 'map' },
      });
      live.addLayer({
        id: 'le-pin', type: 'symbol', source: PIN_SOURCE,
        layout: {
          'icon-image': ['match', ['get', 'kind'], 'preview', PIN_IMAGES.preview.id, PIN_IMAGES.subject.id],
          'icon-anchor': 'bottom', 'icon-size': 1, 'icon-allow-overlap': true, 'icon-ignore-placement': true,
          'icon-pitch-alignment': 'viewport', 'icon-rotation-alignment': 'viewport',
        },
      });
    };
    ensurePinImages(map).then(apply);
  }, []);

  const syncOverlays = useCallback(() => {
    const map = mapRef.current;
    if (!map || !styleReadyRef.current) return;
    const { center: c, radiusMiles: r, showJobRadii: rings } = propsRef.current;

    const removeLayer = (id) => { if (map.getLayer(id)) map.removeLayer(id); };
    const removeSource = (id) => { if (map.getSource(id)) map.removeSource(id); };

    if (!c) {
      ['le-radius-fill', 'le-radius-line', 'le-job-radii-line', 'le-job-radii-label'].forEach(removeLayer);
      ['le-radius', 'le-job-radii', 'le-job-radii-labels'].forEach(removeSource);
      return;
    }

    const point = [c.longitude, c.latitude];
    const radiusFeature = {
      type: 'Feature',
      properties: { radius: r },
      geometry: { type: 'Polygon', coordinates: [circleCoordinates(point, (r || 5) * MILE_METERS)] },
    };
    if (map.getSource('le-radius')) {
      map.getSource('le-radius').setData(radiusFeature);
    } else {
      map.addSource('le-radius', { type: 'geojson', data: radiusFeature });
      map.addLayer({
        id: 'le-radius-fill',
        type: 'fill',
        source: 'le-radius',
        paint: { 'fill-color': RADIUS_RING_COLOR, 'fill-opacity': 0.07 },
      });
      map.addLayer({
        id: 'le-radius-line',
        type: 'line',
        source: 'le-radius',
        paint: { 'line-color': RADIUS_RING_COLOR, 'line-width': 2, 'line-dasharray': [2, 2], 'line-opacity': 0.9 },
      });
    }

    const ringFeatures = JOB_RADII_MILES.map((miles) => ({
      type: 'Feature',
      properties: { miles },
      geometry: { type: 'LineString', coordinates: circleCoordinates(point, miles * MILE_METERS) },
    }));
    const labelFeatures = JOB_RADII_MILES.map((miles) => {
      const ring = circleCoordinates(point, miles * MILE_METERS, 4);
      return { type: 'Feature', properties: { label: `${miles} mi` }, geometry: { type: 'Point', coordinates: ring[0] } };
    });
    const ringsData = { type: 'FeatureCollection', features: ringFeatures };
    const labelsData = { type: 'FeatureCollection', features: labelFeatures };
    if (map.getSource('le-job-radii')) {
      map.getSource('le-job-radii').setData(ringsData);
      map.getSource('le-job-radii-labels').setData(labelsData);
    } else {
      map.addSource('le-job-radii', { type: 'geojson', data: ringsData });
      map.addSource('le-job-radii-labels', { type: 'geojson', data: labelsData });
      map.addLayer({
        id: 'le-job-radii-line',
        type: 'line',
        source: 'le-job-radii',
        paint: { 'line-color': '#2a78d6', 'line-width': 1.2, 'line-opacity': 0.75 },
      });
      map.addLayer({
        id: 'le-job-radii-label',
        type: 'symbol',
        source: 'le-job-radii-labels',
        layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-offset': [0, -0.8], 'text-anchor': 'bottom' },
        paint: { 'text-color': '#2a78d6', 'text-halo-color': '#ffffff', 'text-halo-width': 1.2 },
      });
    }
    const visibility = rings ? 'visible' : 'none';
    map.setLayoutProperty('le-job-radii-line', 'visibility', visibility);
    map.setLayoutProperty('le-job-radii-label', 'visibility', visibility);
  }, []);

  // Map lifecycle.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || FALLBACK_TOKEN;
    // A remount with a cached result (tool switched away and back) opens
    // straight on the subject instead of flying in from the national view.
    const initial = propsRef.current.center;
    if (initial) lastCenterKeyRef.current = `${initial.latitude.toFixed(6)},${initial.longitude.toFixed(6)}`;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE_URLS[mapStyle] || STYLE_URLS.satellite,
      center: initial ? [initial.longitude, initial.latitude] : DEFAULT_CENTER,
      zoom: initial ? RESULT_ZOOM : DEFAULT_ZOOM,
      antialias: true,
      // Touch, where the map sits inside the scrolling column (<=1100px): two fingers move it.
      cooperativeGestures: Boolean(window.matchMedia?.('(max-width: 1100px) and (pointer: coarse)').matches),
      attributionControl: true,
      // Our terrain tile proxy needs the session token; Mapbox cannot await it,
      // so the last token the API client obtained is attached synchronously.
      transformRequest: (url) => {
        if (!isAtlasApiUrl(url)) return { url };
        const token = getCachedIdToken();
        return token ? { url, headers: { Authorization: `Bearer ${token}` } } : { url };
      },
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: true, visualizePitch: true }), 'bottom-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial', maxWidth: 120 }), 'bottom-left');
    map.on('style.load', () => {
      styleReadyRef.current = true;
      syncTopo();
      syncOverlays();
      syncPlaces();
      syncParcel();
      syncTerrain();
      syncCanopy();
      syncFootprints();
      syncFlood();
      syncComps();
      syncPins();
    });
    map.on('click', 'le-pin', (event) => {
      if (propsRef.current.pinMode) return;
      const feature = event.features && event.features[0];
      if (!feature || feature.properties.kind !== 'subject') return;
      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new mapboxgl.Popup({ offset: 30, closeButton: false, closeOnClick: true, className: 'le-popup' })
        .setLngLat(feature.geometry.coordinates).setText(feature.properties.label || '').addTo(map);
    });
    map.on('mouseenter', 'le-pin', () => { if (!propsRef.current.pinMode) map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'le-pin', () => { if (!propsRef.current.pinMode) map.getCanvas().style.cursor = ''; });
    const showPlacePopup = (feature, lngLat) => {
      const props = feature.properties || {};
      const el = document.createElement('div');
      el.className = 'le-place-popup';
      const title = document.createElement('strong');
      title.textContent = props.name || 'Place';
      el.appendChild(title);
      let lines;
      if (props.kind === 'externality') lines = [`${props.type} · ${fmtMiles(Number(props.distance))}`, `Impact ${Number(props.effective).toFixed(0)} (base ${props.base}, distance-decayed)`];
      else if (props.kind === 'public') lines = [`${props.categoryLabel || 'Public place'} · ${fmtMiles(Number(props.distance))}`, ...(props.locality ? [String(props.locality)] : [])];
      else lines = [`${String(props.tier)} · score ${props.score} · ${String(props.subtype || '').replace(/_/g, ' ')}`, `${fmtMiles(Number(props.distance))}${Number(props.locations) > 1 ? ` · ${props.locations} locations in radius` : ''}`];
      if (props.refreshed) lines.push(`Refreshed ${props.refreshed}${props.stale === 'true' || props.stale === true ? ' · stale' : ''}`);
      lines.forEach((text) => { const line = document.createElement('div'); line.textContent = text; el.appendChild(line); });
      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new mapboxgl.Popup({ offset: 12, closeButton: true, closeOnClick: true, className: 'le-popup' }).setLngLat(lngLat).setDOMContent(el).addTo(map);
    };
    ['le-anchors-circle', 'le-externalities-circle', 'le-public-places-circle'].forEach((layerId) => {
      map.on('click', layerId, (event) => {
        const feature = event.features && event.features[0];
        if (feature) showPlacePopup(feature, event.lngLat);
      });
      map.on('mouseenter', layerId, () => { if (!propsRef.current.pinMode) map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layerId, () => { if (!propsRef.current.pinMode) map.getCanvas().style.cursor = ''; });
    });
    // Public places: name · category · distance on hover (uniform dots carry no size cue).
    const publicHover = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10, className: 'le-popup' });
    map.on('mouseenter', 'le-public-places-circle', (e) => {
      const f = e.features && e.features[0];
      if (!f) return;
      const p = f.properties || {};
      publicHover.setLngLat(f.geometry.coordinates).setText(`${p.name || 'Place'} · ${p.categoryLabel || 'Public place'} · ${fmtMiles(Number(p.distance))}`).addTo(map);
    });
    map.on('mouseleave', 'le-public-places-circle', () => publicHover.remove());
    map.on('click', 'le-public-places-circle', () => publicHover.remove());
    // Tree canopy patches: area and tallest point on hover, following the cursor across the patch.
    const canopyHover = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 8, className: 'le-popup' });
    map.on('mousemove', 'le-canopy-fill', (e) => {
      const f = e.features && e.features[0];
      if (!f || propsRef.current.pinMode) return;
      canopyHover.setLngLat(e.lngLat).setText(f.properties.label || 'Canopy').addTo(map);
    });
    map.on('mouseleave', 'le-canopy-fill', () => canopyHover.remove());
    // Flood zones: zone, class and share of the lot on hover.
    const floodHover = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 8, className: 'le-popup' });
    map.on('mousemove', 'le-flood-fill', (e) => {
      const f = e.features && e.features[0];
      if (!f || propsRef.current.pinMode) return;
      floodHover.setLngLat(e.lngLat).setText(f.properties.hover || 'Flood zone').addTo(map);
    });
    map.on('mouseleave', 'le-flood-fill', () => floodHover.remove());
    // Building footprints: area, height and which side of the line on hover.
    const footprintHover = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 8, className: 'le-popup' });
    ['le-footprints-fill', 'le-footprints-surround-fill'].forEach((layerId) => {
      map.on('mousemove', layerId, (e) => {
        const f = e.features && e.features[0];
        if (!f || propsRef.current.pinMode) return;
        footprintHover.setLngLat(e.lngLat).setText(f.properties.label || 'Structure').addTo(map);
      });
      map.on('mouseleave', layerId, () => footprintHover.remove());
    });
    const reportZoom = () => { if (zoomCallbackRef.current) zoomCallbackRef.current(map.getZoom()); };
    map.on('zoomend', reportZoom);
    map.on('load', reportZoom);
    map.on('click', (event) => {
      const { pinMode: active, onPinDrop: drop, onPinModeChange: setPin } = propsRef.current;
      if (!active) return;
      const { lng, lat } = event.lngLat;
      if (setPin) setPin(false);
      if (drop) drop({ latitude: Number(lat.toFixed(6)), longitude: Number(lng.toFixed(6)) });
    });
    map.on('error', (event) => {
      // Style/tiles errors are logged but never crash the tool.
      // eslint-disable-next-line no-console
      console.warn('LandEvaluationMap: mapbox error', event?.error?.message || event);
    });
    mapRef.current = map;
    // Debug/test handle (e.g. `document.querySelector('.le-map-canvas').__leMap`).
    containerRef.current.__leMap = map;
    return () => {
      styleReadyRef.current = false;
      if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Style switch: custom sources/layers are dropped by setStyle and rebuilt on style.load.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleRef.current === mapStyle) return;
    styleRef.current = mapStyle;
    const url = STYLE_URLS[mapStyle] || STYLE_URLS.satellite;
    styleReadyRef.current = false;
    map.setStyle(url);
  }, [mapStyle]);

  // Contour visibility + 3D terrain toggles + exaggeration.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    syncTopo();
    if (!styleReadyRef.current) return;
    // Wheel and pinch zoom default to "around the cursor". With terrain on and
    // the camera pitched, Mapbox ray-casts the cursor against the terrain to
    // find that point; near the horizon the ray misses or hits far ground and
    // the camera lurches instead of zooming in. Zooming around the centre while
    // tilted keeps the motion stable; the flat view keeps the default feel.
    if (map.scrollZoom) map.scrollZoom.enable(terrain3d ? { around: 'center' } : undefined);
    if (map.touchZoomRotate) map.touchZoomRotate.enable(terrain3d ? { around: 'center' } : undefined);
    if (terrain3d) {
      const c = propsRef.current.center;
      const tooFar = map.getZoom && map.getZoom() < BUILDINGS_MIN_ZOOM + 0.5;
      map.easeTo({
        pitch: PITCH_3D,
        bearing: map.getBearing() || -20,
        ...(tooFar && c ? { center: [c.longitude, c.latitude], zoom: 16 } : {}),
        duration: 1100,
      });
    } else if (map.getPitch && map.getPitch() > 0) {
      map.easeTo({ pitch: 0, bearing: 0, duration: 700 });
    }
  }, [showContours, terrain3d, terrainExaggeration, syncTopo]);

  // Places layers follow the dataset, the radius and the toggles.
  useEffect(() => {
    syncPlaces();
  }, [places, placesRadiusMiles, showAnchors, showExternalities, showPublicPlaces, center, syncPlaces]);

  // Parcel / district polygons follow the zoning dataset and its toggles.
  useEffect(() => {
    syncParcel();
  }, [parcel, zoneBoundary, showParcel, showZone, center, syncParcel]);

  // Terrain cells follow the terrain dataset and its toggle. The flood fills are
  // re-slotted afterwards: a rebuilt terrain grid lands on top of them.
  useEffect(() => {
    syncTerrain();
    syncFlood();
  }, [terrain, showTerrain, showParcelContours, terrainMode, maxSlopePercent, center, syncTerrain, syncFlood]);

  // Comps dots follow the comps dataset and its toggle.
  useEffect(() => {
    syncComps();
  }, [comps, showComps, center, syncComps]);

  // Canopy polygons follow the tree_canopy geometry and its toggle.
  useEffect(() => {
    syncCanopy();
  }, [treeCanopy, showTreeCanopy, center, syncCanopy]);

  // Footprint outlines follow the building_footprints dataset and its toggles.
  useEffect(() => {
    syncFootprints();
  }, [buildingFootprints, showFootprints, showNeighbouringFootprints, center, syncFootprints]);

  // Flood zones follow the flood_zone GeoJSON and their toggle.
  useEffect(() => {
    syncFlood();
  }, [flood, showFlood, center, syncFlood]);

  // The contour sweep: an amber highlight runs from the highest contour down
  // to the lowest, once per period. Runs only while there is something to animate.
  useEffect(() => {
    const map = mapRef.current;
    const levelCount = Array.isArray(terrain?.contours?.levels) ? terrain.contours.levels.length : 0;
    if (!map || !showTerrain || !showParcelContours || levelCount === 0) return undefined;
    let frame = 0;
    let lastApplied = null;
    const start = performance.now();
    const tick = (now) => {
      if (!mapRef.current || !styleReadyRef.current || !map.getLayer('le-terrain-contour-glow')) { frame = requestAnimationFrame(tick); return; }
      const phase = ((now - start) % CONTOUR_SWEEP_MS) / CONTOUR_SWEEP_MS;              // 0 → 1 over one sweep
      const pos = Math.round((phase * (levelCount + 1) - 0.5) * 16) / 16;                // -0.5 … levelCount + 0.5, in 1/16 rank steps
      if (pos !== lastApplied) {
        lastApplied = pos;
        const dist = ['abs', ['-', ['get', 'rank'], pos]];
        map.setPaintProperty('le-terrain-contour-glow', 'line-opacity', ['interpolate', ['linear'], dist, 0, 0.95, 0.7, 0.5, 1.5, 0]);
        map.setPaintProperty('le-terrain-contour-glow', 'line-width', ['interpolate', ['linear'], dist, 0, 3.8, 1.5, 1.2]);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [terrain, showTerrain, showParcelContours]);

  // "Show on map" from the Nearby places card.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusPlace) return;
    const point = [focusPlace.longitude, focusPlace.latitude];
    map.flyTo({ center: point, zoom: Math.max(map.getZoom ? map.getZoom() : FOCUS_ZOOM, FOCUS_ZOOM), essential: true, duration: 1100 });
    const el = document.createElement('div');
    el.className = 'le-place-popup';
    const title = document.createElement('strong');
    title.textContent = focusPlace.label || 'Place';
    el.appendChild(title);
    if (focusPlace.detail) { const line = document.createElement('div'); line.textContent = focusPlace.detail; el.appendChild(line); }
    if (popupRef.current) popupRef.current.remove();
    popupRef.current = new mapboxgl.Popup({ offset: 12, closeButton: true, closeOnClick: true, className: 'le-popup' }).setLngLat(point).setDOMContent(el).addTo(map);
  }, [focusPlace]);

  // Subject marker + camera.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!center) {
      pinStateRef.current = { ...pinStateRef.current, subject: null, label: '' };
      lastCenterKeyRef.current = null;
      syncPins();
      syncOverlays();
      return;
    }
    const point = [center.longitude, center.latitude];
    pinStateRef.current = { subject: point, preview: null, label: address || `${center.latitude.toFixed(5)}, ${center.longitude.toFixed(5)}` };
    syncPins();

    const key = `${center.latitude.toFixed(6)},${center.longitude.toFixed(6)}`;
    if (lastCenterKeyRef.current !== key) {
      lastCenterKeyRef.current = key;
      const bounds = parcelBounds(parcel);
      if (bounds) {
        map.fitBounds(bounds, { padding: 70, maxZoom: PARCEL_MAX_ZOOM, duration: 1800, essential: true });
      } else {
        map.flyTo({ center: point, zoom: RESULT_ZOOM, essential: true, duration: 1800 });
      }
    }
    syncOverlays();
  }, [center, address, radiusMiles, showJobRadii, parcel, syncOverlays, syncPins]);

  // Autocomplete preview: peek at the candidate before evaluating.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !previewCenter || center) return;
    const point = [previewCenter.longitude, previewCenter.latitude];
    pinStateRef.current = { ...pinStateRef.current, preview: point };
    syncPins();
    map.flyTo({ center: point, zoom: PREVIEW_ZOOM, essential: true, duration: 1200 });
  }, [previewCenter, center, syncPins]);

  // Pin mode cursor.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = pinMode ? 'crosshair' : '';
  }, [pinMode]);

  const recenter = useCallback(() => {
    const map = mapRef.current;
    const { center: c, parcel: parcelGeom } = propsRef.current;
    if (!map || !c) return;
    const bounds = parcelBounds(parcelGeom);
    if (bounds) map.fitBounds(bounds, { padding: 70, maxZoom: PARCEL_MAX_ZOOM, duration: 900, pitch: 0 });
    else map.flyTo({ center: [c.longitude, c.latitude], zoom: RESULT_ZOOM, essential: true, duration: 900 });
  }, []);

  const fitRadius = useCallback(() => {
    const map = mapRef.current;
    const { center: c, radiusMiles: r } = propsRef.current;
    if (!map || !c) return;
    const ring = circleCoordinates([c.longitude, c.latitude], (r || 5) * MILE_METERS, 16);
    const bounds = ring.reduce((b, coord) => b.extend(coord), new mapboxgl.LngLatBounds(ring[0], ring[0]));
    map.fitBounds(bounds, { padding: 32, duration: 900, pitch: 0 });
  }, []);

  useImperativeHandle(ref, () => ({ recenter, fitRadius }), [recenter, fitRadius]);

  return (
    <div className={`le-map ${pinMode ? 'le-map-pinning' : ''} ${terrain3d ? 'le-map-3d' : ''}`}>
      <div ref={containerRef} className="le-map-canvas" />
      {pinMode ? <div className="le-map-hint">Click anywhere on the map to evaluate that point</div> : null}
    </div>
  );
});

export default LandEvaluationMap;
